// modules
import { useCallback, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Form } from "antd";

// service calls
import { getAdhocAdminReport } from "services/api/reimbursement";

// components
import CountDownModal from "components/molecules/ModalContainer/CountDownModalContainer";

// constants and types
import { Routes, ADHOC_REPORTS as Constants } from "constants/global";
import { FormInstance } from "antd/es/form/Form";

type AdhocReportPropTypes = {
	fileLocation: string;
	fileLocationError: boolean;
	handleSMFileLocation: (e: { target: { id: any; value: any } }) => void;
	proceedToExportReport: () => void;
	loading: boolean;
	form: FormInstance;
};

export function useAdhocReportPage(): AdhocReportPropTypes {
	const navigate = useNavigate();
	const [form] = Form.useForm();

	const [fileLocation, setFileLocation] = useState<string>("");
	const [loading, setLoading] = useState<boolean>(false);
	const [fileLocationError, setFileLocationErr] = useState<boolean>(false);

	const handleSMFileLocation = useCallback(
		(e: { target: { id: any; value: any } }) => {
			setFileLocation(e?.target?.value);
			if (fileLocationError) {
				setFileLocationErr(false);
			}
		},
		[fileLocation, fileLocationError]
	);

	const onSuccess = useCallback(() => {
		navigate(Routes.REPORT_ADHOC);
	}, [navigate]);

	const onFailure = useCallback(
		(error: any) => {
			setLoading(false);
			CountDownModal({
				timer: 3,
				errorIcon: true,
				title: error?.message || Constants.NO_DATA_FOUND,
				onTimeComplete: onSuccess
			});
		},
		[loading]
	);

	const proceedToExportReport = useCallback(async () => {
		if (fileLocation) {
			try {
				setLoading(true);

				const { data: reportsData } = await getAdhocAdminReport({ smp_file_location: fileLocation });
				const { errors, data } = reportsData;

				if (data && !errors?.length) {
					setLoading(false);

					CountDownModal({
						timer: 3,
						errorIcon: data !== Constants.SUCCESS,
						title: data,
						onTimeComplete: onSuccess
					});

					if (data === Constants.SUCCESS) {
						form.resetFields();
						setFileLocation("");
						setFileLocationErr(false);
					} else {
						setFileLocationErr(true);
					}
				} else if (errors?.length) {
					onFailure(errors[0]);
				}
			} catch (error: any) {
				if (error?.response?.status === 401) {
					onFailure(error?.response?.data);
				} else {
					onFailure({ message: Constants.SERVER_ERR });
				}
			}
		}
	}, [fileLocation, fileLocationError, loading, setFileLocation]);

	return {
		fileLocation,
		fileLocationError,
		loading,
		form,
		handleSMFileLocation,
		proceedToExportReport
	};
}
